import React, { useRef, useEffect, useState } from 'react';
import { DimensionData } from './AlignmentDataCalculator';

interface AlignmentRadarChartProps {
  dimensionData: DimensionData[];
  targetLabel?: string;
  currentLabel?: string;
  size?: number;
}

// Updated colors
const TARGET_USER_COLOR = "rgba(99, 102, 241, 0.9)"; // indigo-500
const TARGET_USER_FILL = "rgba(99, 102, 241, 0.5)"; // indigo-500 with transparency
const CURRENT_USER_COLOR = "rgba(0, 82, 134, 0.9)"; // eoe-blue
const CURRENT_USER_FILL = "rgba(0, 82, 134, 0.7)"; // eoe-blue with transparency
const DEFAULT_TEXT_COLOR = "#666";
const HIGH_MATCH_COLOR = "#16a34a"; // green-600

const AlignmentRadarChart: React.FC<AlignmentRadarChartProps> = ({
  dimensionData,
  targetLabel = "Them",
  currentLabel = "You",
  size: propSize
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState<number>(propSize || 180);

  useEffect(() => {
    if (propSize) {
      setChartSize(propSize);
      return;
    }

    const updateChartSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = window.innerHeight * 0.5; // 50vh
        const newSize = Math.min(containerWidth - 16, containerHeight);
        setChartSize(Math.max(180, newSize)); // Minimum 180px
      }
    };

    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, [propSize]);

  const renderChart = () => {
    const size = chartSize;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size * 0.35;
    const baseFontSize = Math.max(4, size / 46);

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Grid lines */}
        <circle cx={centerX} cy={centerY} r={radius * 0.125} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
        <circle cx={centerX} cy={centerY} r={radius * 0.25} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
        <circle cx={centerX} cy={centerY} r={radius * 0.5} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
        <circle cx={centerX} cy={centerY} r={radius * 0.75} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#888" strokeWidth="0.5" strokeOpacity="0.5" />
        
        {/* Dimension axes */}
        {dimensionData.map((_, i) => {
          const angle = (i * 2 * Math.PI) / dimensionData.length;
          const x = centerX + radius * Math.sin(angle);
          const y = centerY - radius * Math.cos(angle);
          
          return (
            <line 
              key={`axis-${i}`}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="#888"
              strokeWidth="0.5"
              strokeOpacity="0.5"
            />
          );
        })}
        
        {/* Current user polygon */}
        <polygon
          points={dimensionData.map((dim, i) => {
            const angle = (i * 2 * Math.PI) / dimensionData.length;
            const value = dim.currentValue ?? 0;
            const distance = (radius * 0.125) + ((value + 2) / 4) * (radius * 0.875);
            const x = centerX + distance * Math.sin(angle);
            const y = centerY - distance * Math.cos(angle);
            return `${x},${y}`;
          }).join(' ')}
          fill={CURRENT_USER_FILL}
          stroke={CURRENT_USER_COLOR}
          strokeWidth="1.5"
        />
        
        {/* Target user polygon */}
        <polygon
          points={dimensionData.map((dim, i) => {
            const angle = (i * 2 * Math.PI) / dimensionData.length;
            const value = dim.targetValue ?? 0;
            const distance = (radius * 0.125) + ((value + 2) / 4) * (radius * 0.875);
            const x = centerX + distance * Math.sin(angle);
            const y = centerY - distance * Math.cos(angle);
            return `${x},${y}`;
          }).join(' ')}
          fill={TARGET_USER_FILL}
          stroke={TARGET_USER_COLOR}
          strokeWidth="1.5"
        />
        
        {/* Dimension labels */}
        {dimensionData.map((dim, i) => {
          const angle = (i * 2 * Math.PI) / dimensionData.length;
          let labelDistance = radius * 1.1;
          let xOffset = 0;
          let yOffset = 0;
          
          // Adjust positions for specific dimensions
          if (i === 6 || i === 2) {
            labelDistance = radius * 0.8;
          } else if (i === 1) {
            yOffset = -radius * 0.1;
            xOffset = -radius * 0.26;
          } else if (i === 4) {
            xOffset = radius * 0.2;
            yOffset = -radius * 0.05;
          } else if (i === 7) {
            labelDistance = radius * 1.3;
          }
          
          const x = centerX + labelDistance * Math.sin(angle) + xOffset;
          const y = centerY - labelDistance * Math.cos(angle) + yOffset;
          
          // Text anchor based on position
          const quadrant = Math.floor((angle + Math.PI / 8) / (Math.PI / 4)) % 8;
          let textAnchor = "middle";
          if (quadrant === 0 || quadrant === 7) textAnchor = "middle";
          else if (quadrant >= 1 && quadrant <= 3) textAnchor = "start";
          else if (quadrant >= 4 && quadrant <= 6) textAnchor = "end";
          
          // Determine label colors and show viewed user's sided percentages next to labels
          const targetValue = dim.targetValue;
          const currentValue = dim.currentValue;
          const alignment = dim.alignment;
          
          const onSameSide = (targetValue !== null && currentValue !== null) && 
                             ((targetValue < 0 && currentValue < 0) || 
                              (targetValue > 0 && currentValue > 0));
          
          let label1Color = DEFAULT_TEXT_COLOR;
          let label2Color = DEFAULT_TEXT_COLOR;
          
          if (targetValue !== null && targetValue < 0) {
            label1Color = TARGET_USER_COLOR;
          }
          
          if (currentValue !== null && currentValue > 0) {
            label2Color = CURRENT_USER_COLOR;
          }
          
          if (onSameSide) {
            if (targetValue !== null && targetValue < 0) {
              label1Color = HIGH_MATCH_COLOR;
            } else if (targetValue !== null && targetValue > 0) {
              label2Color = HIGH_MATCH_COLOR;
            }
          }
          
          const alignmentColor = typeof alignment === "number" && alignment > 70 ? HIGH_MATCH_COLOR : DEFAULT_TEXT_COLOR;
          
          return (
            <g key={`label-${i}`}>
              <text
                x={x}
                y={y - baseFontSize * 0.7}
                fontSize={baseFontSize}
                fontWeight="500"
                textAnchor={textAnchor}
                dominantBaseline="middle"
                fill={label1Color}
              >
                {dim.label1}
                {typeof dim.targetLabel1Pct === 'number' ? ` ${dim.targetLabel1Pct}%` : ''}
              </text>
              
              <text
                x={x}
                y={y + baseFontSize * 0.7}
                fontSize={baseFontSize}
                fontWeight="500"
                textAnchor={textAnchor}
                dominantBaseline="middle"
                fill={label2Color}
              >
                {dim.label2}
                {typeof dim.targetLabel2Pct === 'number' ? ` ${dim.targetLabel2Pct}%` : ''}
              </text>
              
              <text
                x={x}
                y={y + baseFontSize * 2}
                fontSize={baseFontSize * 0.8}
                fontWeight="bold"
                textAnchor={textAnchor}
                dominantBaseline="middle"
                fill={alignmentColor}
              >
                {typeof alignment === "number" && alignment > 0 ? `${alignment}%` : ""}
              </text>
            </g>
          );
        })}
        
        {/* Center point */}
        <circle cx={centerX} cy={centerY} r={1.5} fill="#666" />
      </svg>
    );
  };

  return (
    <div className="flex justify-center py-2" ref={containerRef}>
      {renderChart()}
    </div>
  );
};

export default AlignmentRadarChart;